import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import useAuthStore from '../stores/authStore';
import Input from '../components/ui/Input';
import Logo from '../components/shared/Logo';
import { ShieldCheck } from 'lucide-react';

const loginSchema = z.object({
  email: z.string().email('Bitte geben Sie eine gueltige E-Mail-Adresse ein'),
  password: z.string().min(1, 'Passwort ist erforderlich')
});

export default function Login() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const verifyTOTP = useAuthStore((s) => s.verifyTOTP);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // TOTP step
  const [totpStep, setTotpStep] = useState(false);
  const [totpToken, setTotpToken] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const totpInputRef = useRef(null);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(loginSchema)
  });

  useEffect(() => {
    if (totpStep && totpInputRef.current) {
      totpInputRef.current.focus();
    }
  }, [totpStep]);

  const onSubmit = async (values) => {
    setError('');
    setLoading(true);
    try {
      const result = await login(values.email, values.password);
      if (result.requiresTOTP) {
        setTotpToken(result.totpToken);
        setTotpStep(true);
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const onTotpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyTOTP(totpToken, totpCode);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Code ungueltig');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-50 dark:bg-slate-950 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="xl" />
          </div>
          <p className="text-gray-500 dark:text-slate-400 mt-2">
            {totpStep ? 'Zwei-Faktor-Authentifizierung' : 'Melden Sie sich an'}
          </p>
        </div>

        {!totpStep ? (
          <form onSubmit={handleSubmit(onSubmit)} className="bg-white dark:bg-slate-900 p-8 rounded-xl shadow-lg dark:shadow-slate-900/50 border border-gray-200 dark:border-slate-700 space-y-4">
            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}

            <Input
              label="E-Mail"
              type="email"
              autoComplete="email"
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label="Passwort"
              type="password"
              autoComplete="current-password"
              error={errors.password?.message}
              {...register('password')}
            />

            <button type="submit" disabled={loading} className="btn-metallic w-full">
              {loading ? 'Anmelden...' : 'Anmelden'}
            </button>

            <div className="text-center pt-2">
              <Link to="/reset-password" className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300">
                Passwort vergessen?
              </Link>
            </div>
          </form>
        ) : (
          <form onSubmit={onTotpSubmit} className="bg-white dark:bg-slate-900 p-8 rounded-xl shadow-lg dark:shadow-slate-900/50 border border-gray-200 dark:border-slate-700 space-y-4">
            <div className="flex justify-center mb-2">
              <ShieldCheck className="w-12 h-12 text-teal-600 dark:text-teal-400" />
            </div>

            <p className="text-sm text-gray-600 dark:text-slate-400 text-center">
              Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.
            </p>

            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}

            <input
              ref={totpInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full text-center text-2xl tracking-[0.5em] font-mono border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-3 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
            />

            <button type="submit" disabled={loading || totpCode.length !== 6} className="btn-metallic w-full">
              {loading ? 'Pruefen...' : 'Bestaetigen'}
            </button>

            <button
              type="button"
              onClick={() => { setTotpStep(false); setTotpToken(null); setTotpCode(''); setError(''); }}
              className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Zurueck zur Anmeldung
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
